import { useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, X, ImagePlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import { assetUrl } from "@/context/SettingsContext";

/**
 * Upload widget. Value = stored path/url, onChange(url) called after upload.
 * `folder` = "logos" | "images"
 */
export default function ImageUpload({ label, value, onChange, folder = "images", accept = "image/*", height = 160, testid = "image-upload" }) {
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(value || "");

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File too large. Max 20MB.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/uploads?folder=${encodeURIComponent(folder)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.url);
      setManual(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testid}>
      {label && <Label className="text-slate-700 text-sm">{label}</Label>}
      <div className="mt-1.5 flex flex-col sm:flex-row gap-3">
        <div
          className="relative rounded-xl border border-dashed border-slate-300 bg-slate-50 grid place-items-center overflow-hidden shrink-0"
          style={{ width: 200, height }}
        >
          {value ? (
            <>
              <img src={assetUrl(value)} alt="Preview" className="w-full h-full object-contain bg-white" />
              <button
                type="button"
                aria-label="Remove"
                onClick={() => { onChange?.(""); setManual(""); }}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black/80"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <div className="text-slate-400 flex flex-col items-center gap-2 text-xs">
              <ImagePlus className="w-6 h-6" />
              <span>No image</span>
            </div>
          )}
        </div>
        <div className="flex-1 flex flex-col gap-2 min-w-0">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <Input
              type="file"
              accept={accept}
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid={`${testid}-input`}
            />
            <Button type="button" asChild variant="outline" className="rounded-full h-10">
              <span>{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Upload file</span>
            </Button>
          </label>
          <div className="flex gap-2">
            <Input
              placeholder="…or paste an image URL"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              className="h-10 rounded-lg border-slate-200"
              data-testid={`${testid}-url`}
            />
            <Button
              type="button"
              variant="outline"
              className="rounded-lg h-10"
              onClick={() => onChange?.(manual)}
              data-testid={`${testid}-set-url`}
            >
              Set
            </Button>
          </div>
          <p className="text-xs text-slate-500">JPG, PNG, WEBP, or SVG · 20MB max · original quality preserved</p>
        </div>
      </div>
    </div>
  );
}
